import { GmgTheme, IconDataFilled } from '@gmg/gmg-react-components';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AppContext from 'src/AppContext';
import styled, { useTheme } from 'styled-components';
import FolderDark from '../../assets/svg/graphic_folder-dark-255.svg';
import FolderLight from '../../assets/svg/graphic_folder-light-255.svg';
import useSort from '../../customHooks/useSort';
import { useListFolders } from '../../graphql/useListFolders';
import {
    Table,
    TableContainer,
    TableContainerCardStyle,
    Thead,
    Tr,
} from '../../shared/TableStyle';
import { FolderListItem } from '../../viewModels/Folder';
import OrganizationContainer from '../OrganizationContainer';
import EmptyPage from '../shared/EmptyPage';
import AddFolderBtn from './AddFolderBtn';
import FolderRow from './FolderRow';
import { sortFolders } from './sortFolders';
export interface RequestedFolder {
    id: string;
    level: number;
}
export interface DataManagementProps { }

const DataManagement: FC<DataManagementProps> = () => {
    const { t } = useTranslation();
    const theme: GmgTheme = useTheme();
    const { onError } = useContext(AppContext);
    const { orgId } = useParams();
    const [folders, setFolders] = useState<Array<FolderListItem> | undefined>();
    const [groups, setGroups] = useState<Map<string, string> | undefined>();
    const [requestedFolders, setRequestedFolders] = useState<Array<RequestedFolder>>([]);
    const [selectedFolder, setSelectedFolder] = useState<FolderListItem | undefined>();
    const { getSortFn } = useSort();
    const queryResults = useListFolders(requestedFolders);

    useEffect(() => {
        if (!queryResults) return;

        queryResults.forEach(async (result) => {
            if (!result.data) {
                if (result.error) {
                    onError(result!.error as Error);
                }
                return;
            }

            setFolders((state) => [...state!, ...result.data.folders]);

            // remove the folder from the list of folders that should be queries
            setRequestedFolders((state) =>
                state.filter((f) => f.id !== result.data.parentFolderId),
            );

            // Old comment: result.remove(); // same effect as queryClient.invalidateQueries(['listFolders', parentFolderId])
            // result.remove() is not supported anymore. I don't think we need to do anything here.
        });
    }, [queryResults, onError]);

    const handleToggleExpand = (requestedFolder: FolderListItem) => {
        setFolders((state) =>
            state!.map((f: FolderListItem) => ({
                ...f,
                isExpanded:
                    f.id === requestedFolder.id
                        ? !f.isExpanded // => toggle
                        : f.isExpanded, // => leave untouched
            })),
        );

        if (!requestedFolder.isExpanded) {
            setRequestedFolders((state) => [...state, { id: requestedFolder.id, level: requestedFolder.level }]);
        } else {
            const idsToBeThrownOut: Array<string> = [];

            // folder gets closed => remove the subfolders from the state
            setFolders((state) => {
                const recurse = (folderId: string) => {
                    state!
                        .filter((item) => item.parentFolderId === folderId)
                        .forEach((item) => {
                            idsToBeThrownOut.push(item.id);
                            recurse(item.id);
                        });
                };

                recurse(requestedFolder.id);

                if (selectedFolder && idsToBeThrownOut.includes(selectedFolder.id)) {
                    setSelectedFolder(undefined);
                }

                return state!.filter((item) => idsToBeThrownOut.indexOf(item.id) === -1);
            });
        }
    };

    const handleToggleSelection = (folder: FolderListItem) => {
        setSelectedFolder(selectedFolder?.id === folder.id ? undefined : folder);
    };

    const handleNewFolder = (newFolder: FolderListItem) => {
        const parentFolder = folders!.find(f => f.id === newFolder.parentFolderId);

        if (!parentFolder || parentFolder.isExpanded) { // if it's a root folder or the parent is expanded, then just add the new folder
            setFolders(state => [...state!, newFolder]);
        } else { // otherwise, reload the parent folder
            setRequestedFolders(state => [...state, { id: parentFolder.id, level: parentFolder.level }]);
        }

        if (!parentFolder) return;

        setFolders((prevState) => {
            return [
                ...prevState!.filter((folder) => folder.id !== parentFolder!.id),
                {
                    ...parentFolder,
                    isExpanded: true,
                },
            ];
        });
    };

    const handleEditFolder = (updatedFolder: FolderListItem) => {
        setFolders(prevState => [
            ...prevState!.filter(f => f.id !== updatedFolder.id),
            updatedFolder,
        ]);
    };

    const handleDeleteFolder = (id: string) => {
        setFolders(prevState => prevState!.filter(f => f.id !== id));
    };

    const iconEmptyPage = theme.type === 'dark'
        ? <FolderDark width={255} height={255} />
        : <FolderLight width={255} height={255} />;

    return (
        <OrganizationContainer
            button={
                <AddFolderBtn
                    parentFolder={selectedFolder}
                    onNewFolder={handleNewFolder}
                    siblingFolderNames={folders
                        ? folders
                            .filter(folder => folder.parentFolderId === selectedFolder?.id)
                            .map(f => f.name)
                        : []
                    }
                    orgId={orgId!}
                    groups={groups!}
                    disabled={!folders}
                />
            }
            onOrganizationLoad={(organization) => {
                setFolders(organization.rootFolders);
                setGroups(new Map(organization.groups.map(g => [g.id, g.name])));
            }}
            viewInfo={{
                icon: <IconDataFilled width={16} height={16} />,
                label: t('HeaderPageTitles.data_management_title', 'Data Management'),
            }}
        >
            <TableContainer>
                {!folders ? (
                    <></>
                    // Quick fix not to see the spinner 'cos at that point we have already fetched the data
                    // and there is actually no loading state but react needs a tiny amount of time to update the ui
                    // React.suspense should be introduced with fallback UI
                ) : folders.length === 0 ? (
                    <EmptyPage
                        image={iconEmptyPage}
                        title={t('EmptyFolderPage.pageTitle_title', 'Add a new folder to create a cloud structure for your organization\'s data!')}
                        description={t('EmptyFolderPage.information_desc', 'Users can\'t save or import data until they have access to a folder, created by an admin.')}
                    />
                ) : (
                    <TableContainerCardStyle aria-label='TableContainerCardStyle' className='vertical-scrollbar horizontal-scrollbar'>
                        <Table aria-label='Data Management'>
                            <StyledThead>
                                <Tr isSelected={false} isHeaderRow>
                                    <th className='px12_600_normal'>{t('DataManagement.tableLabels.name_title', 'Name')}</th>
                                    <th></th>
                                </Tr>
                            </StyledThead>
                            <tbody>
                                {sortFolders(folders!, getSortFn).map((folder: FolderListItem) => (
                                    <FolderRow
                                        key={folder.id}
                                        folder={folder}
                                        isLoading={requestedFolders.some((f) => f.id === folder.id)}
                                        onToggleExpand={() => { handleToggleExpand(folder); }}
                                        isSelected={folder.id === selectedFolder?.id}
                                        onToggleSelection={() => { handleToggleSelection(folder); }}
                                        onDeleteFolder={() => { handleDeleteFolder(folder.id); }}
                                        onEditFolder={handleEditFolder}
                                        siblingFolderNames={folders!
                                            .filter(f => f.parentFolderId === folder.parentFolderId && f.id !== folder.id)
                                            .map(f => f.name)}
                                        groups={groups!}
                                        orgId={orgId!}
                                    />
                                ))}
                            </tbody>
                        </Table>
                    </TableContainerCardStyle>
                )}
            </TableContainer >
        </OrganizationContainer >
    );
};

const StyledThead = styled(Thead)`
   th {
    &:first-child {
      padding-left: 40px;
    }
  }
`;

export default DataManagement;